import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Flame, Anvil, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Steel = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/steel"]?.title} description={seoData["industries/steel"]?.description} path="/industries/steel" keywords={seoData["industries/steel"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Steel & Metalworking"
        description="Coating and repair for steel mills and metalworking facilities. High-temperature coatings, steel coating, erosion protection, and equipment repair for steel production environments."
        backgroundKey="steel"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['High-temperature corrosion and oxidation on furnace components',
              'Severe erosion on material handling equipment and ducts',
              'Cooling water system corrosion and scale buildup',
              'Concrete floor degradation from heat, impact, and chemical exposure',
              'Crane rail and structural steel corrosion',
              'Dust collection and exhaust system wear'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-red-50 rounded-lg">
                <Flame className="text-red-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-red-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Anvil className="text-red-600 mr-3 flex-shrink-0" size={22} />
                  High-Temperature Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Ceramic and metallic coating systems for furnace components, flues, and exhaust systems operating at extreme temperatures. Thermal barrier coatings for energy efficiency and component protection.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Anvil className="text-red-600 mr-3 flex-shrink-0" size={22} />
                  Erosion Protection Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Abrasion-resistant lining systems for conveyors, chutes, hoppers, and ductwork handling scale, slag, and raw materials. Ceramic-filled coatings for extreme wear environments.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Anvil className="text-red-600 mr-3 flex-shrink-0" size={22} />
                  Cooling Water System Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Pipe coating, heat exchanger repair, and cooling tower restoration for mill water systems. Corrosion protection for circulating water infrastructure.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Anvil className="text-red-600 mr-3 flex-shrink-0" size={22} />
                  Structural & Floor Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Heavy-duty floor coatings for mill floors, crane runways, and maintenance areas. Structural steel coating and concrete repair for production buildings.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Steel & Metalworking?</h2>
              <div className="space-y-3">
                {['High-temperature coating systems rated for extreme heat environments',
                  'Extreme abrasion resistance for material handling applications',
                  'Shutdown and outage scheduling experience',
                  'Heavy industrial safety training and certification',
                  'Fast-cure systems for time-critical outage work',
                  'On-site mobile blasting for surface preparation'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-red-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Heavy Industry Specialists</h3>
              <p className="text-red-100 mb-6">BCS has the experience and coating systems for the extreme demands of steel and metalworking environments. High temperature, high abrasion, high performance.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/equipment-repair')}>
                View Equipment Repair <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Steel Mill Assets</h2>
          <p className="text-blue-100 mb-8">Contact us to plan coating and repair work around your next mill outage.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Steel;
